import classNames from 'classnames';
import React, { memo, useMemo } from 'react';
import { getText, getTextStyle } from './helpers';
import { ITextProps, TextPreset } from './types';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';
import './style.scss';

/**
 * @see TextPreset
 * @param type
 * @property {section-header} - section header, for example "MAIN DETAILS" in item edit screen.
 * @property {block-header} - block header, for example "Order type" in item edit screen.
 * @property {switcher-label} - switcher label.
 * @property {column-title}- column heading in list, for example "ITEM" in items list.
 * @property {list-row} - text in list row, for example items name, category in items list.
 * @property {donatable-in-list} - "donatable" label in list row.
 * @property {error} - error text.
 * @property {recommendation} - recommendation text, example meal create/edit no size/groups.
 * @property {cart-option} - name of option of modifier or combo item from product in cart.
 * @property {order-field} - field label in checkout screen.
 * @property {modal-message} - message in ModalMessage.
 * @property {title-inside-input} - for prevComponent in Input component.
 */
const Text: React.FC<ITextProps> = (props: ITextProps): JSX.Element => {
  const text: string | number = useMemo(() => getText(props), [props]);
  const textClassName: string = useMemo(() => getTextStyle(props), [props]);

  let textElement: JSX.Element | undefined;

  switch (props.type) {
    case TextPreset.SECTION_HEADER:
      textElement = (
        <h2 dir={props.dir} className={textClassName}>
          {text}
        </h2>
      );
      break;
    case TextPreset.COLUMN_TITLE:
      textElement = (
        <span dir={props.dir} className={textClassName}>
          {text}
        </span>
      );
      break;
    case TextPreset.LIST_ROW:
      textElement = (
        <span dir={props.dir} style={props.style} className={textClassName}>
          {text}
        </span>
      );
      break;
    default:
      textElement = (
        <p dir={props.dir} className={textClassName}>
          {text}
        </p>
      );
      break;
  }

  if (props.tooltip) {
    const { iconSize = 18, ...tooltipProps } = props.tooltip;

    return (
      <div className={classNames('d-flex align-items-center gap-10', props.wrapperClassName)}>
        {textElement}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <TooltipIcon {...tooltipProps} iconSize={iconSize} />
      </div>
    );
  }

  return textElement;
};

export default memo(Text);
export { TextPreset as TEXT };
